/**
 * @file api.ts
 * @brief Query/mutation hooks for the Asana connector.
 *
 * @details
 * Reads are queries; connecting, choosing a project, mapping a person and
 * pushing are mutations — pushing especially, since it writes into someone
 * else's system and must never fire on a mount, a refocus or a cache refetch.
 *
 * @dependencies
 * - @tanstack/react-query, shared/api/client
 *
 * @references
 * - BACKEND/src/features/connectors/routes.js
 *
 * @author DES
 * @date 05-08-2026
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@/shared/api/client";
import {
  ConnectorStatusSchema,
  AsanaProjectsSchema,
  AsanaWorkspacesSchema,
  AsanaPeopleSchema,
  AsanaPushResultSchema,
  type AsanaPushResult,
} from "./schemas";

const base = "/api/connectors";
export const connectorKeys = {
  status: ["connector-status"] as const,
  workspaces: ["asana-workspaces"] as const,
  projects: ["asana-projects"] as const,
  people: ["asana-people"] as const,
};

export function useConnectorStatus() {
  return useQuery({
    queryKey: connectorKeys.status,
    queryFn: async () => ConnectorStatusSchema.parse(await apiGet(`${base}/status`)),
    staleTime: 30_000,
  });
}

/** Only fetched once connected — Asana is asked nothing before that. */
export function useAsanaProjects(enabled: boolean) {
  return useQuery({
    queryKey: connectorKeys.projects,
    queryFn: async () =>
      AsanaProjectsSchema.parse(await apiGet(`${base}/asana/projects`)),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/** The workspaces this Asana account can reach. Connected accounts only. */
export function useAsanaWorkspaces(enabled: boolean) {
  return useQuery({
    queryKey: connectorKeys.workspaces,
    queryFn: async () =>
      AsanaWorkspacesSchema.parse(await apiGet(`${base}/asana/workspaces`)),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * Switching workspace clears the destination project and the people mappings
 * server-side — both belonged to the workspace being left. The cached copies
 * must go with them, or the page would keep offering a project list and a set of
 * Asana members from an account view that is no longer in effect.
 */
export function useSetAsanaWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { workspaceId: string }) =>
      apiPost(`${base}/asana/workspace`, vars),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectorKeys.status });
      qc.removeQueries({ queryKey: connectorKeys.projects });
      qc.removeQueries({ queryKey: connectorKeys.people });
    },
  });
}

export function useAsanaPeople(enabled: boolean) {
  return useQuery({
    queryKey: connectorKeys.people,
    queryFn: async () => AsanaPeopleSchema.parse(await apiGet(`${base}/asana/people`)),
    enabled,
    staleTime: 60_000,
  });
}

export function useAsanaAuthorizeUrl() {
  return useMutation({
    mutationFn: async () => {
      const raw = (await apiGet(`${base}/asana/authorize`)) as { url?: string };
      if (!raw?.url) throw new Error("Asana is not configured on this server.");
      return raw.url;
    },
  });
}

export function useSetAsanaProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { projectId: string; projectName?: string }) =>
      apiPost(`${base}/asana/project`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectorKeys.status }),
  });
}

export function useMapAsanaPerson() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: {
      userId: string;
      asanaUserId: string | null;
      asanaUserName?: string | null;
    }) => apiPost(`${base}/asana/people`, vars),
    onSuccess: () => qc.invalidateQueries({ queryKey: connectorKeys.people }),
  });
}

export function useDisconnectAsana() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete(`${base}/asana`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: connectorKeys.status });
      qc.removeQueries({ queryKey: connectorKeys.people });
      qc.removeQueries({ queryKey: connectorKeys.projects });
    },
  });
}

export function usePushMeetingToAsana() {
  return useMutation<AsanaPushResult, unknown, { meetingId: string }>({
    mutationFn: async ({ meetingId }) =>
      AsanaPushResultSchema.parse(
        await apiPost(`${base}/asana/push/${encodeURIComponent(meetingId)}`, {}),
      ),
  });
}
